import { useState, useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { getMonth, formatDateKey } from '@/lib/liturgical/calendar';
import { getColorHex, getRankName } from '@/lib/liturgical/colors';
import type { LiturgicalDay } from '@/lib/liturgical/types';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function CalendarPage() {
  useSeoMeta({
    title: 'Liturgical Calendar — Nostr Missal',
    description: 'Browse the Roman Catholic liturgical calendar by month.',
  });

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const days = useMemo(() => getMonth(year, month), [year, month]);

  const prev = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };
  const goNow = () => { const n = new Date(); setYear(n.getFullYear()); setMonth(n.getMonth()); };
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-stone-800 dark:text-stone-200 sm:text-3xl" style={{ fontFamily: '"Cinzel", serif' }}>
            Liturgical Calendar
          </h1>
        </div>

        {/* Month Navigation */}
        <div className="mb-5 flex items-center justify-between rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-4 py-3">
          <Button variant="ghost" size="sm" onClick={prev} className="gap-1 text-stone-500 hover:text-stone-900 dark:hover:text-stone-100">
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline text-[13px]">Prev</span>
          </Button>

          <div className="text-center">
            <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-200" style={{ fontFamily: '"Cinzel", serif' }}>
              {MONTHS[month]} {year}
            </h2>
            {!isCurrentMonth && (
              <button onClick={goNow} className="mt-0.5 text-[11px] tracking-wider uppercase text-amber-600 dark:text-amber-500 hover:text-amber-700 transition-colors">
                Back to today
              </button>
            )}
          </div>

          <Button variant="ghost" size="sm" onClick={next} className="gap-1 text-stone-500 hover:text-stone-900 dark:hover:text-stone-100">
            <span className="hidden sm:inline text-[13px]">Next</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Day rows */}
        <div className="space-y-1">
          {days.map((day) => (
            <DayRow key={formatDateKey(day.date)} day={day} isToday={same(day.date, now)} />
          ))}
        </div>
      </div>
    </Layout>
  );
}

function DayRow({ day, isToday }: { day: LiturgicalDay; isToday: boolean }) {
  const colorHex = getColorHex(day.color);
  const dn = day.date.getDate();
  const dow = day.date.getDay();
  const isSun = dow === 0;
  const isBold = day.rank === 'solemnity' || day.rank === 'feast';

  return (
    <Link to={`/mass/${formatDateKey(day.date)}`} className="block group">
      <div className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 transition-all duration-150 group-hover:bg-stone-100 dark:group-hover:bg-stone-800 ${
        isToday
          ? 'border-stone-400 dark:border-stone-500 bg-stone-50 dark:bg-stone-900 ring-1 ring-stone-400 dark:ring-stone-500'
          : 'border-stone-100 dark:border-stone-800 bg-white dark:bg-stone-900'
      }`}>
        {/* Vestment color dot */}
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: colorHex }} />

        {/* Day of week + date number */}
        <div className="w-12 shrink-0">
          <span className={`block text-[9px] font-bold uppercase tracking-widest ${isSun ? 'text-amber-600 dark:text-amber-500' : 'text-stone-400 dark:text-stone-500'}`}>
            {DOW[dow]}
          </span>
          <span className={`text-sm font-bold ${isToday ? 'text-amber-600 dark:text-amber-500' : isSun ? 'text-stone-700 dark:text-stone-200' : 'text-stone-600 dark:text-stone-400'}`}>
            {dn}
          </span>
        </div>

        {/* Name */}
        <span className={`flex-1 truncate text-sm ${isBold ? 'font-semibold text-stone-800 dark:text-stone-100' : isSun ? 'font-medium text-stone-700 dark:text-stone-200' : 'text-stone-600 dark:text-stone-400'}`}>
          {day.name}
        </span>

        {/* Tags */}
        <div className="flex items-center gap-1.5 shrink-0">
          {day.rank !== 'weekday' && (
            <span className="hidden rounded-full bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 px-2 py-0.5 text-[10px] font-medium text-stone-600 dark:text-stone-400 sm:inline">
              {getRankName(day.rank)}
            </span>
          )}
          {day.holyDayOfObligation && (
            <span className="rounded-full bg-red-100 dark:bg-red-900 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:text-red-300">
              HDO
            </span>
          )}
          <ChevronRight className="h-3.5 w-3.5 text-stone-300 dark:text-stone-600 group-hover:text-stone-500 transition-colors" />
        </div>
      </div>
    </Link>
  );
}

function same(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
